/**
 * Project Analytics Dashboard
 * 
 * Displays AC generation metrics, quality trends, and usage statistics.
 */

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  Zap,
  FileText,
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

// ============================================
// Types
// ============================================

interface UsageMetrics {
  projectId: string;
  period: string;
  periodStart: string;
  totalGenerations: number;
  successfulGenerations: number;
  failedGenerations: number;
  avgGenerationTimeMs: number;
  p50GenerationTimeMs: number;
  p95GenerationTimeMs: number;
  avgQualityScore: number;
  minQualityScore: number;
  maxQualityScore: number;
  budgetModeCount: number;
  standardModeCount: number;
  premiumModeCount: number;
  ragEnabledCount: number;
  avgRagCitations: number;
  totalACGenerated: number;
  avgACPerGeneration: number;
}

interface QualityTrend {
  date: string;
  avgQualityScore: number;
  generationCount: number;
  avgTimeMs: number;
}

interface ErrorBreakdown {
  stage: string;
  count: number;
  lastOccurred: string;
}

interface AppClassUsage {
  classId: string;
  count: number;
  avgQualityScore: number;
}

interface DashboardData {
  weekMetrics: UsageMetrics | null;
  monthMetrics: UsageMetrics | null;
  qualityTrend: QualityTrend[];
  recentErrors: ErrorBreakdown[];
  appClassUsage: AppClassUsage[];
}

// ============================================
// Helper Components
// ============================================

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendValue,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-neutral-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-neutral-900">{value}</p>
          {subtitle && (
            <p className="mt-1 text-xs text-neutral-500">{subtitle}</p>
          )}
        </div>
        <div className="p-2 bg-primary-50 rounded-lg">
          <Icon className="w-5 h-5 text-primary-600" />
        </div>
      </div>
      {trend && trendValue && (
        <div className="mt-3 flex items-center text-sm">
          {trend === 'up' && (
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
          )}
          {trend === 'down' && (
            <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
          )}
          <span
            className={
              trend === 'up'
                ? 'text-green-600'
                : trend === 'down'
                ? 'text-red-600'
                : 'text-neutral-500'
            }
          >
            {trendValue}
          </span>
        </div>
      )}
    </Card>
  );
}

function QualityScoreBar({ score, label }: { score: number; label?: string }) {
  const getColor = (s: number) => {
    if (s >= 80) return 'bg-green-500';
    if (s >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-3">
      {label && <span className="text-sm text-neutral-600 w-20">{label}</span>}
      <div className="flex-1 bg-neutral-200 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all ${getColor(score)}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-sm font-medium text-neutral-700 w-10 text-right">
        {score}
      </span>
    </div>
  );
}

function MiniChart({ data }: { data: QualityTrend[] }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-neutral-400 text-sm">
        No data available
      </div>
    );
  }

  const maxScore = Math.max(...data.map((d) => d.avgQualityScore), 100);
  const maxCount = Math.max(...data.map((d) => d.generationCount), 1);

  return (
    <div className="h-32 flex items-end gap-1">
      {data.map((point) => (
        <div
          key={point.date}
          className="flex-1 flex flex-col items-center gap-1"
          title={`${point.date}: Score ${point.avgQualityScore}, ${point.generationCount} generations`}
        >
          {/* Quality score bar */}
          <div
            className="w-full bg-primary-500 rounded-t opacity-80 hover:opacity-100 transition-opacity"
            style={{
              height: `${(point.avgQualityScore / maxScore) * 80}%`,
              minHeight: '4px',
            }}
          />
          {/* Generation count indicator */}
          <div
            className="w-2 h-2 rounded-full bg-neutral-400"
            style={{
              opacity: 0.3 + (point.generationCount / maxCount) * 0.7,
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function ProjectAnalyticsPage() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const setHeader = usePageHeaderStore((state) => state.setHeader);

  useEffect(() => {
    setHeader(
      'Analytics',
      'AC generation metrics and quality trends',
      <BarChart3 className="w-6 h-6" />
    );
  }, [orgId, projectId, setHeader]);

  const {
    data: dashboard,
    isLoading,
    error,
    refetch,
  } = useQuery<DashboardData>({
    queryKey: ['projectAnalytics', projectId],
    queryFn: async () => {
      const res = await api.get<{ data: DashboardData }>(`/analytics/projects/${projectId}/dashboard`);
      return res.data;
    },
    enabled: !!projectId,
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <div className="flex items-center gap-2 text-red-800">
          <AlertTriangle className="w-5 h-5" />
          <p>Failed to load analytics: {(error as Error).message}</p>
        </div>
        <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  const week = dashboard?.weekMetrics;
  const month = dashboard?.monthMetrics;
  const trend = dashboard?.qualityTrend || [];
  const errors = dashboard?.recentErrors || [];
  const appUsage = dashboard?.appClassUsage || [];

  const successRate = week
    ? Math.round((week.successfulGenerations / week.totalGenerations) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <Link
          to={`/app/organizations/${orgId}/projects/${projectId}`}
          className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Project
        </Link>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* No Data State */}
      {!week && !month && (
        <Card className="p-8 text-center">
          <BarChart3 className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-700 mb-2">
            No Analytics Data Yet
          </h3>
          <p className="text-neutral-500 max-w-md mx-auto">
            Start generating acceptance criteria to see metrics and quality trends
            here.
          </p>
        </Card>
      )}

      {/* Week Overview */}
      {week && (
        <>
          <h2 className="text-lg font-semibold text-neutral-800">
            This Week Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Generations"
              value={week.totalGenerations}
              subtitle={`${week.successfulGenerations} successful`}
              icon={FileText}
            />
            <MetricCard
              title="Success Rate"
              value={`${successRate}%`}
              subtitle={`${week.failedGenerations} failed`}
              icon={successRate >= 90 ? CheckCircle : AlertTriangle}
              trend={successRate >= 90 ? 'up' : successRate >= 70 ? 'neutral' : 'down'}
              trendValue={successRate >= 90 ? 'Excellent' : successRate >= 70 ? 'Good' : 'Needs attention'}
            />
            <MetricCard
              title="Avg Quality Score"
              value={week.avgQualityScore}
              subtitle={`Range: ${week.minQualityScore} - ${week.maxQualityScore}`}
              icon={TrendingUp}
            />
            <MetricCard
              title="Avg Generation Time"
              value={`${(week.avgGenerationTimeMs / 1000).toFixed(1)}s`}
              subtitle={`P95: ${(week.p95GenerationTimeMs / 1000).toFixed(1)}s`}
              icon={Clock}
            />
          </div>
        </>
      )}

      {/* Quality Trend Chart */}
      {trend.length > 0 && (
        <Card className="p-5">
          <h3 className="text-md font-semibold text-neutral-800 mb-4">
            Quality Trend (Last 14 Days)
          </h3>
          <MiniChart data={trend} />
          <div className="mt-2 flex justify-between text-xs text-neutral-500">
            <span>{trend[0]?.date}</span>
            <span>{trend[trend.length - 1]?.date}</span>
          </div>
        </Card>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Mode Distribution */}
        {week && (
          <Card className="p-5">
            <h3 className="text-md font-semibold text-neutral-800 mb-4">
              Pipeline Mode Distribution
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" /> Budget
                </span>
                <span className="text-sm font-medium">{week.budgetModeCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-500" /> Standard
                </span>
                <span className="text-sm font-medium">{week.standardModeCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-500" /> Premium
                </span>
                <span className="text-sm font-medium">{week.premiumModeCount}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-neutral-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-600">RAG Enabled</span>
                <span className="font-medium">
                  {week.ragEnabledCount} ({Math.round((week.ragEnabledCount / week.totalGenerations) * 100)}%)
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-neutral-600">Avg Citations</span>
                <span className="font-medium">{week.avgRagCitations}</span>
              </div>
            </div>
          </Card>
        )}

        {/* Recent Errors */}
        <Card className="p-5">
          <h3 className="text-md font-semibold text-neutral-800 mb-4">
            Recent Errors (Last 7 Days)
          </h3>
          {errors.length === 0 ? (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span>No errors in the last 7 days!</span>
            </div>
          ) : (
            <div className="space-y-3">
              {errors.map((err) => (
                <div
                  key={err.stage}
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                >
                  <div>
                    <span className="text-sm font-medium text-red-800">
                      {err.stage}
                    </span>
                    <p className="text-xs text-red-600 mt-1">
                      Last: {new Date(err.lastOccurred).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-red-700">
                    {err.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Application Class Usage */}
      {appUsage.length > 0 && (
        <Card className="p-5">
          <h3 className="text-md font-semibold text-neutral-800 mb-4">
            Application Class Usage (Last 30 Days)
          </h3>
          <div className="space-y-3">
            {appUsage.map((cls) => (
              <div key={cls.classId} className="flex items-center gap-4">
                <span className="text-sm text-neutral-700 w-32 truncate">
                  {cls.classId}
                </span>
                <div className="flex-1">
                  <QualityScoreBar score={cls.avgQualityScore} />
                </div>
                <span className="text-sm text-neutral-500 w-16 text-right">
                  {cls.count} uses
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Month Summary */}
      {month && (
        <Card className="p-5 bg-neutral-50">
          <h3 className="text-md font-semibold text-neutral-800 mb-4">
            Monthly Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-neutral-900">
                {month.totalGenerations}
              </p>
              <p className="text-sm text-neutral-500">Total Generations</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">
                {month.totalACGenerated}
              </p>
              <p className="text-sm text-neutral-500">ACs Generated</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">
                {month.avgQualityScore}
              </p>
              <p className="text-sm text-neutral-500">Avg Quality</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">
                {month.avgACPerGeneration}
              </p>
              <p className="text-sm text-neutral-500">Avg AC/Generation</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
